import { shortDid } from "../util";

type Node = { did: string; type: string };
type Edge = { from: string; to: string; kind: string };
const COLOUR: Record<string, string> = { peer: "var(--peer)", affiliated: "var(--affiliated)", light_issuer: "var(--issuer)", issuer: "var(--issuer)" };
const LABEL: Record<string, string> = { peer: "Peer", affiliated: "Affiliated", light_issuer: "Organisation", issuer: "Issuer" };

/** Ego-centred graph: the holder in the middle, attesters around. Three type colours
 *  only; anchors drawn as a diamond. A list alternative follows for screen readers. */
export function ChainGraph({ centre, nodes, edges }: { centre: string; nodes: Node[]; edges: Edge[] }) {
  const others = nodes.filter((n) => n.did !== centre);
  const R = 120, C = 160;
  const pos = new Map<string, [number, number]>([[centre, [C, C]]]);
  others.forEach((n, i) => {
    const a = (i / Math.max(others.length, 1)) * Math.PI * 2 - Math.PI / 2;
    pos.set(n.did, [C + R * Math.cos(a), C + R * Math.sin(a)]);
  });
  return (
    <div className="graph card">
      <svg viewBox="0 0 320 320" role="img" aria-label="Chain graph">
        {edges.map((e, i) => {
          const [x1, y1] = pos.get(e.from) ?? [C, C], [x2, y2] = pos.get(e.to) ?? [C, C];
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--faint)" strokeWidth={e.kind === "anchor" ? 3 : 1.5} />;
        })}
        {others.map((n) => {
          const [x, y] = pos.get(n.did)!;
          const anchor = edges.some((e) => e.from === n.did && e.kind === "anchor");
          return anchor
            ? <path key={n.did} d={`M${x} ${y - 14} L${x + 14} ${y} L${x} ${y + 14} L${x - 14} ${y}Z`} fill={COLOUR[n.type]} />
            : <circle key={n.did} cx={x} cy={y} r={11} fill={COLOUR[n.type] ?? "var(--muted)"} />;
        })}
        <circle cx={C} cy={C} r={16} fill="var(--text)" />
      </svg>
      <div className="legend">{["peer", "affiliated", "issuer"].map((t) => <span key={t}><i style={{ background: COLOUR[t] }} />{LABEL[t]}</span>)}<span>◆ anchor</span></div>
      <ul className="sr">{edges.map((e, i) => <li key={i}>{LABEL[nodes.find((n) => n.did === e.from)?.type ?? "peer"]} {shortDid(e.from)} — {e.kind}</li>)}</ul>
    </div>
  );
}
